// Robust external link opener — works both in normal browsers and inside
// the Emergent preview iframe (which sandbox-blocks popups).
//
// Strategy:
// • http(s) URLs when we are inside an iframe → prefer navigating the TOP frame
//   (so the user actually leaves the preview and lands on the destination).
//   If cross-origin blocks top navigation, fall back to window.open, then to
//   navigating the current iframe.
// • http(s) URLs when NOT inside an iframe → open a new tab via window.open.
// • Non-http protocols (tel:, mailto:, sms:) → drive the OS handler by setting
//   window.top.location (or window.location if top is cross-origin blocked).

const NON_HTTP_PROTOCOL = /^(tel:|mailto:|sms:|whatsapp:)/i;

export function isInIframe() {
  try {
    return typeof window !== "undefined" && window.top !== window;
  } catch (_) {
    return true;
  }
}

function tryNavigateTop(url) {
  try {
    if (window.top && window.top !== window) {
      window.top.location.href = url;
      return true;
    }
  } catch (_) {
    /* cross-origin — top.location not accessible */
  }
  return false;
}

function tryOpenPopup(url) {
  try {
    const win = window.open(url, "_blank", "noopener,noreferrer");
    return !!win;
  } catch (_) {
    return false;
  }
}

export function openExternal(url) {
  // tel: / mailto: / sms: — OS handler
  if (NON_HTTP_PROTOCOL.test(url)) {
    if (tryNavigateTop(url)) return;
    window.location.href = url;
    return;
  }

  const inIframe = isInIframe();
  if (inIframe) {
    // Prefer escaping the iframe by navigating the top frame.
    if (tryNavigateTop(url)) return;
    // If cross-origin block, try popup.
    if (tryOpenPopup(url)) return;
    // Last resort: navigate current iframe (visible in dev preview).
    window.location.href = url;
    return;
  }

  // Not in iframe: open a new tab.
  if (tryOpenPopup(url)) return;
  window.location.href = url;
}

export function externalLinkHandler(url) {
  return (e) => {
    e.preventDefault();
    openExternal(url);
  };
}

// Choose the correct anchor `target` at runtime.
// - Inside the Emergent preview iframe → "_top" so a plain click also escapes
//   the sandbox even if JS is prevented.
// - Standalone tab / production → "_blank" so we open a new tab.
export function externalTarget() {
  return isInIframe() ? "_top" : "_blank";
}
